/**
 * Bundle 118 - Survey Console Overview tab.
 *
 * A single-glance dashboard so an event manager can answer "how is
 * my survey doing" without leaving the page. Renders:
 *   - Progress cards (total invited, sent, delivered, opened,
 *     clicked, responded, response-rate)
 *   - Recent responses feed (last 10, excludes Bundle 117 preview
 *     writes on the backend)
 *   - 14-day submissions sparkline
 *   - "As of" timestamp so no tile ever masquerades as fresh
 *
 * Data source: GET /api/admin/events/{event_id}/survey/overview
 * (backend, `@bundle(118)`). All numbers filter to the ACTIVE
 * campaign only; archived campaigns never leak in.
 *
 * No em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  RefreshCw, Send, Star, MailCheck, MailOpen,
  MousePointerClick, Clock, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';

const KPI = ({ label, value, hint, testid, icon: Icon }) => (
  <div
    className="rounded-lg border border-neutral-200 bg-white
                    px-4 py-3 flex items-start gap-3"
    data-testid={testid}
  >
    {Icon ? (
      <div className="rounded-md bg-primary-50 p-2 mt-0.5">
        <Icon className="w-4 h-4 text-primary-800" />
      </div>
    ) : null}
    <div>
      <div className="text-[10px] uppercase tracking-widest
                            text-neutral-500 font-semibold">
        {label}
      </div>
      <div className="text-2xl font-bold text-primary-900
                            tabular-nums mt-0.5"
                data-testid={`${testid}-value`}>
        {value}
      </div>
      {hint ? (
        <div className="text-[11px] text-neutral-500 mt-0.5">
          {hint}
        </div>
      ) : null}
    </div>
  </div>
);

const StatusBadgeClass = {
  submitted: 'bg-success-100 text-success-800 border-success-200',
  in_progress: 'bg-amber-100 text-amber-800 border-amber-200',
  not_started: 'bg-neutral-100 text-neutral-700 border-neutral-200',
};

const Sparkline = ({ series, testid }) => {
  const max = Math.max(1, ...series.map((s) => s.submitted));
  const w = 320;
  const h = 60;
  const step = series.length > 1 ? w / (series.length - 1) : 0;
  const points = series.map((s, i) => {
    const y = h - (s.submitted / max) * (h - 4) - 2;
    const x = i * step;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const totalSubs = series.reduce((a, s) => a + s.submitted, 0);
  return (
    <div className="rounded-lg border border-neutral-200 bg-white
                          px-4 py-3"
              data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest
                              text-neutral-500 font-semibold">
          Submissions, last 14 days
        </div>
        <div className="text-xs text-neutral-600">
          <span className="font-bold text-primary-900 tabular-nums">
            {totalSubs}
          </span>{' '}
          total
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`}
                  className="w-full h-16"
                  preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke="#1e3a5f"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={points}
        />
      </svg>
      <div className="flex justify-between text-[10px]
                            text-neutral-500 mt-1">
        <span>{series[0]?.date?.slice(5) || ''}</span>
        <span>{series[series.length - 1]?.date?.slice(5) || ''}</span>
      </div>
    </div>
  );
};

const RatingBar = ({ dist }) => {
  const total = dist.reduce((a, r) => a + r.count, 0) || 1;
  return (
    <div className="rounded-lg border border-neutral-200 bg-white
                          px-4 py-3"
              data-testid="overview-rating-distribution">
      <div className="text-[10px] uppercase tracking-widest
                            text-neutral-500 font-semibold mb-2">
        Overall rating breakdown
      </div>
      <div className="space-y-1.5">
        {[...dist].reverse().map((row) => {
          const pct = Math.round(100 * row.count / total);
          return (
            <div key={row.rating}
                        className="flex items-center gap-2 text-xs">
              <span className="w-6 text-neutral-600 tabular-nums">
                {row.rating}
                <Star className="w-3 h-3 inline text-amber-500
                                        fill-amber-500 ml-0.5" />
              </span>
              <div className="flex-1 h-2 bg-neutral-100 rounded">
                <div
                  className="h-2 rounded bg-primary-800"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="w-8 text-right tabular-nums
                                      text-neutral-700">
                {row.count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const SurveyOverviewTab = ({
  eventId, onOpenResponse, onSendTest, onSendLive, onSendReminder,
  busy, campaignStatus,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stuckDialog, setStuckDialog] = useState(null);
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(
        `/admin/events/${eventId}/survey/overview`,
      );
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                        || e?.response?.data?.detail
                        || 'Could not load overview.');
    } finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const openStuckDialog = async () => {
    setApplying(true);
    try {
      const { data: d } = await api.post(
        '/admin/incident/finalize-stuck-submissions',
        { event_id: eventId, dry_run: true },
      );
      setStuckDialog(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                        || e?.response?.data?.detail
                        || 'Dry-run failed');
    } finally { setApplying(false); }
  };

  const applyStuck = async () => {
    if (!window.confirm(
      `Finalize ${stuckDialog?.would_finalize || 0} stuck submissions? `
      + 'This flips their status to submitted using the exact '
      + 'answers already autosaved on the server.',
    )) return;
    setApplying(true);
    try {
      const { data: d } = await api.post(
        '/admin/incident/finalize-stuck-submissions',
        { event_id: eventId, dry_run: false },
      );
      toast.success(
        `Finalized ${d.finalized} row(s). `
        + `${d.still_incomplete_count} still incomplete.`,
      );
      setStuckDialog(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message
                        || 'Apply failed');
    } finally { setApplying(false); }
  };

  if (!data) {
    return (
      <div className="p-8 text-center text-neutral-500 text-sm"
                  data-testid="overview-loading">
        Loading overview...
      </div>
    );
  }

  const c = data.counts || {};
  return (
    <div className="space-y-4" data-testid="survey-overview-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-xs text-neutral-600
                              flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest
                                  text-neutral-500 font-semibold">
            Active campaign
          </span>
          <code className="font-mono text-[11px]">
            {data.campaign?.id_short}
          </code>
          <Badge variant="outline">{data.campaign?.status}</Badge>
          {data.campaign?.question_set_refreshed_bundle ? (
            <Badge variant="outline"
                          data-testid="overview-qset-bundle-badge">
              Question set: bundle{' '}
              {data.campaign.question_set_refreshed_bundle}
            </Badge>
          ) : null}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="text-[11px] text-neutral-500"
                      data-testid="overview-last-computed">
            As of{' '}
            <time dateTime={data.last_computed_at}>
              {new Date(data.last_computed_at).toLocaleString()}
            </time>
          </div>
          <Button size="sm" variant="outline" onClick={load}
                        disabled={loading}
                        data-testid="overview-refresh-btn">
            <RefreshCw className={
              `w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`
            } />
            Refresh
          </Button>
        </div>
      </div>

      {/* Actions row */}
      <div className="rounded-lg border border-primary-200
                            bg-primary-50/60 p-3 flex flex-wrap gap-2
                            items-center"
                data-testid="overview-actions">
        <div className="text-[10px] uppercase tracking-widest
                              text-primary-900 font-semibold mr-2">
          Send actions
        </div>
        <Button size="sm" variant="outline"
                      onClick={onSendTest} disabled={busy}
                      data-testid="overview-send-test-btn">
          <Send className="w-3.5 h-3.5 mr-1" />
          Send test to me
        </Button>
        <Button size="sm" variant="outline"
                      onClick={onSendReminder}
                      disabled={busy
                                    || campaignStatus === 'paused'}
                      data-testid="overview-send-reminder-btn"
                      title={campaignStatus === 'paused'
                                ? 'Resume the campaign first' : ''}>
          <MailCheck className="w-3.5 h-3.5 mr-1" />
          Reminder to unopened
        </Button>
        <Button size="sm"
                      onClick={onSendLive} disabled={busy}
                      className="bg-primary-900 text-white
                                      hover:bg-primary-800"
                      data-testid="overview-send-live-btn"
                      title="Sends invitations to every participant on the active campaign who has not received one yet">
          <Send className="w-3.5 h-3.5 mr-1" />
          Send to all outstanding
        </Button>
        {(c.stuck_finalizable || 0) > 0 ? (
          <Button size="sm" variant="outline"
                        onClick={openStuckDialog}
                        disabled={applying}
                        className="border-amber-400 text-amber-900
                                        hover:bg-amber-50"
                        data-testid="overview-finalize-stuck-btn"
                        title="Submits on behalf of respondents who autosaved a complete answer set but were blocked by the pre-Bundle-119 400.">
            <Star className="w-3.5 h-3.5 mr-1" />
            Finalize stuck ({c.stuck_finalizable})
          </Button>
        ) : null}
      </div>

      {/* Bundle 124 - always-visible stuck-row diagnostic panel.
          Bundle 122 rendered this as an inline span in the toolbar,
          which wrapped off-screen on some prod viewports. It now
          lives here so it is unmissable regardless of layout. */}
      {data?.stuck_finalizable_debug ? (
        <div className="rounded-md border border-neutral-200
                              bg-neutral-50 px-3 py-2 text-xs
                              text-neutral-700 flex flex-wrap
                              items-center gap-2"
                    data-testid="overview-stuck-diagnostic-panel">
          <span className="text-[10px] uppercase tracking-widest
                                  text-neutral-500 font-semibold">
            Stuck-row diagnostic
          </span>
          <span data-testid="overview-stuck-in-progress-total">
            in_progress={data.stuck_finalizable_debug.in_progress_total}
          </span>
          <span data-testid="overview-stuck-would-pass">
            would_pass={data.stuck_finalizable_debug.would_pass}
          </span>
          {Object.entries(
            data.stuck_finalizable_debug
              .still_incomplete_by_reason || {},
          ).map(([k, v]) => (
            <span key={k}
                    className="rounded bg-white border
                                    border-neutral-200 px-1.5 py-0.5
                                    text-[10px]"
                    data-testid={`overview-stuck-reason-${k}`}>
              {k}={v}
            </span>
          ))}
          {(data.stuck_finalizable_debug.in_progress_total || 0) === 0
            && (c.stuck_finalizable || 0) === 0 ? (
            <span className="ml-auto text-[10px] text-neutral-500"
                          data-testid="overview-stuck-no-rows-hint">
              Nobody has autosaved a response yet on this campaign.
            </span>
          ) : (data.stuck_finalizable_debug.in_progress_total || 0) > 0
            && (c.stuck_finalizable || 0) === 0 ? (
            <span className="ml-auto text-[10px] text-neutral-500"
                          data-testid="overview-stuck-none-passable-hint">
              {data.stuck_finalizable_debug.in_progress_total}{' '}
              autosaved but none currently pass Bundle 119 validation.
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Progress cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3"
                data-testid="overview-progress-cards">
        <KPI label="Total invited" value={c.total_invited}
                  hint="active campaign only"
                  icon={Send}
                  testid="overview-kpi-total-invited" />
        <KPI label="Sent" value={c.sent}
                  hint={`${c.failed || 0} failed`}
                  icon={MailCheck}
                  testid="overview-kpi-sent" />
        <KPI label="Opened" value={c.opened}
                  hint={c.total_invited
                    ? `${Math.round(
                      100 * c.opened / (c.sent || 1),
                    )}% open rate`
                    : ''}
                  icon={MailOpen}
                  testid="overview-kpi-opened" />
        <KPI label="Clicked" value={c.clicked}
                  hint=""
                  icon={MousePointerClick}
                  testid="overview-kpi-clicked" />
        <KPI label="Responded" value={c.responded}
                  hint={`${c.in_progress} in progress`}
                  icon={Star}
                  testid="overview-kpi-responded" />
        <KPI label="Response rate"
                  value={`${c.response_rate_pct}%`}
                  hint="responded / invited"
                  icon={TrendingUp}
                  testid="overview-kpi-response-rate" />
        <KPI label="Delivered" value={c.delivered}
                  hint=""
                  icon={Clock}
                  testid="overview-kpi-delivered" />
        <KPI label="Failed sends" value={c.failed}
                  hint={c.failed > 0
                    ? 'check Participants tab' : ''}
                  icon={Send}
                  testid="overview-kpi-failed" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Sparkline series={data.submissions_14d || []}
                            testid="overview-sparkline" />
        <RatingBar dist={data.rating_distribution || []} />
      </div>

      {/* Recent responses feed */}
      <div className="rounded-lg border border-neutral-200
                            bg-white p-3"
                data-testid="overview-recent-responses">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest
                                text-neutral-500 font-semibold">
            Recent responses
            <span className="ml-2 text-[9px]
                                      text-emerald-800 bg-emerald-100
                                      px-1.5 py-0.5 rounded"
                        data-testid="admin-unmasked-indicator">
              Admin view - unmasked
            </span>
          </div>
          <div className="text-[11px] text-neutral-500">
            Excludes admin preview and test rows.
          </div>
        </div>
        {(data.recent_responses || []).length === 0 ? (
          <div className="text-sm text-neutral-500 p-4 text-center"
                      data-testid="overview-no-responses">
            No responses yet. Once a recipient opens the survey and
            saves a step, their row appears here.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-neutral-500 border-b">
                <th className="py-2 pr-2">Recipient</th>
                <th className="py-2 pr-2">Variant</th>
                <th className="py-2 pr-2">Rating</th>
                <th className="py-2 pr-2">Status</th>
                <th className="py-2 pr-2">Updated</th>
                <th className="py-2 pr-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.recent_responses.map((r) => (
                <tr key={r.response_id}
                          className="border-b border-neutral-100
                                          hover:bg-neutral-50"
                          data-testid={
                            `overview-recent-row-${r.response_id_short}`
                          }>
                  <td className="py-2 pr-2">
                    <div className="font-medium text-primary-900">
                      {r.recipient_name}
                    </div>
                    <div className="font-mono text-[10px]
                                          text-neutral-500">
                      {r.email || r.email_masked}
                    </div>
                  </td>
                  <td className="py-2 pr-2">
                    <Badge variant="outline"
                                className="text-[10px]">
                      {r.variant}
                    </Badge>
                  </td>
                  <td className="py-2 pr-2">
                    {r.overall_rating ? (
                      <span className="inline-flex items-center
                                              gap-0.5">
                        {r.overall_rating}
                        <Star className="w-3 h-3 text-amber-500
                                                fill-amber-500" />
                      </span>
                    ) : (
                      <span className="text-neutral-400">-</span>
                    )}
                  </td>
                  <td className="py-2 pr-2">
                    <Badge className={`text-[10px] border
                                              ${StatusBadgeClass[r.status]
                                                || StatusBadgeClass.not_started}`}>
                      {r.status.replace('_', ' ')}
                    </Badge>
                  </td>
                  <td className="py-2 pr-2 text-neutral-600">
                    {r.updated_at
                      ? new Date(r.updated_at).toLocaleString()
                      : '-'}
                  </td>
                  <td className="py-2 pr-2">
                    {onOpenResponse ? (
                      <Button
                        size="sm" variant="outline"
                        onClick={() => onOpenResponse(r.response_id)}
                        data-testid={
                          `overview-open-response-btn-${r.response_id_short}`
                        }
                      >
                        View
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {stuckDialog ? (
        <div className="fixed inset-0 z-50 flex items-center
                              justify-center bg-black/40 p-4"
                    data-testid="finalize-stuck-dialog"
                    onClick={() => setStuckDialog(null)}>
          <div className="bg-white rounded-lg max-w-3xl w-full
                                max-h-[80vh] overflow-y-auto p-5"
                      onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-primary-900">
                Finalize stuck submissions
              </h2>
              <button onClick={() => setStuckDialog(null)}
                            className="text-neutral-500 text-sm">
                Close
              </button>
            </div>
            <div className="text-xs text-neutral-700 mb-3
                                  rounded-md border border-amber-200
                                  bg-amber-50 p-3">
              Runs Bundle 119&apos;s fixed validation on every
              autosaved-but-not-submitted row. Passing rows will
              flip to submitted. Answers are NOT modified; only
              status and submitted_at.
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs mb-3">
              <div className="rounded border p-2">
                <div className="text-[10px] uppercase
                                        text-neutral-500">
                  Candidates
                </div>
                <div className="text-xl font-bold">
                  {stuckDialog.candidates_total}
                </div>
              </div>
              <div className="rounded border border-emerald-300
                                    bg-emerald-50 p-2">
                <div className="text-[10px] uppercase
                                        text-emerald-800">
                  Would finalize
                </div>
                <div className="text-xl font-bold text-emerald-900"
                            data-testid="stuck-would-finalize-count">
                  {stuckDialog.would_finalize}
                </div>
              </div>
              <div className="rounded border border-red-300
                                    bg-red-50 p-2">
                <div className="text-[10px] uppercase text-red-800">
                  Still incomplete
                </div>
                <div className="text-xl font-bold text-red-900">
                  {stuckDialog.still_incomplete_count}
                </div>
              </div>
            </div>
            {(stuckDialog.would_finalize_rows || []).length > 0 ? (
              <div className="mb-3">
                <div className="text-[10px] uppercase
                                        text-neutral-500 mb-1">
                  Will finalize
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-neutral-500 border-b">
                      <th className="py-1">Recipient</th>
                      <th className="py-1">Email</th>
                      <th className="py-1">Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stuckDialog.would_finalize_rows.map((r) => (
                      <tr key={r.response_id}
                                className="border-b border-neutral-100">
                        <td className="py-1">{r.recipient_name}</td>
                        <td className="py-1 font-mono">{r.email}</td>
                        <td className="py-1 text-neutral-500">
                          {r.updated_at
                            ? new Date(r.updated_at).toLocaleString()
                            : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {(stuckDialog.still_incomplete_rows || []).length > 0 ? (
              <div className="mb-3">
                <div className="text-[10px] uppercase
                                        text-neutral-500 mb-1">
                  Still incomplete (won&apos;t be finalized)
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-neutral-500 border-b">
                      <th className="py-1">Recipient</th>
                      <th className="py-1">Missing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stuckDialog.still_incomplete_rows.map((r) => (
                      <tr key={r.response_id}
                                className="border-b border-neutral-100">
                        <td className="py-1">{r.recipient_name}</td>
                        <td className="py-1 font-mono text-[10px]
                                              text-red-800">
                          {(r.failing_rules || []).join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline"
                            onClick={() => setStuckDialog(null)}>
                Cancel
              </Button>
              <Button size="sm" onClick={applyStuck}
                            disabled={applying
                                          || stuckDialog.would_finalize === 0}
                            className="bg-primary-900 text-white
                                            hover:bg-primary-800"
                            data-testid="stuck-apply-btn">
                Apply finalize
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SurveyOverviewTab;
